import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import './i18n'; // Initialize i18n
import '@/App.css';

// Desktop Pages
import AuthPage from './pages/AuthPage';
import SeekerDashboard from './pages/SeekerDashboard';
import ProviderDashboard from './pages/ProviderDashboard';
import ChatInterface from './pages/ChatInterface';
import VoiceCallInterface from './pages/VoiceCallInterface';
import BookAppointment from './pages/BookAppointment';
import SeekerIntakePage from './pages/SeekerIntakePage';
import { ForgotPasswordPage, ResetPasswordPage } from './pages/PasswordResetPages';
import AppFooter from './components/AppFooter';
import PrivacyRequestsPage from './pages/PrivacyRequestsPage';
import AdminPrivacyRequestsPage from './pages/AdminPrivacyRequestsPage';

// Mobile Pages
import MobileChatList from './pages/mobile/MobileChatList';
import MobileChatInterface from './pages/mobile/MobileChatInterface';
import MobileCareOptions from './pages/mobile/MobileCareOptions';
import ProfileSettings from './pages/mobile/ProfileSettings';

// Admin & Info Pages
import AdminDashboard from './pages/AdminDashboard';
import TeleManasInfo from './pages/TeleManasInfo';
import ProviderSearch from './pages/ProviderSearch';
import VideoCallPage from './pages/VideoCallPage';

// MVP Pages (JSS)
import AdminUsersPage from './pages/AdminUsersPage';
import AdminSettingsPage from './pages/AdminSettingsPage';
import AdminWellnessPartnersPage from './pages/AdminWellnessPartnersPage';
import AdminPaymentsPage from './pages/AdminPaymentsPage';
import AdminSchedulingPage from './pages/AdminSchedulingPage';
import CareSeekerBridge from './pages/CareSeekerBridge';
import GuestChatEntry from './pages/GuestChatEntry';
import EscalationsPage from './pages/EscalationsPage';
import MyResourcesPage from './pages/MyResourcesPage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// PWA service worker REMOVED — the cache-first SW served a stale index.html after
// redeploys (referencing old hashed JS chunks) causing a blank page in production.
// Actively unregister any previously-installed SW and clear its caches so existing
// clients recover. The /service-worker.js file is now self-unregistering too.
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.getRegistrations()
      .then((regs) => regs.forEach((r) => r.unregister()))
      .catch(() => {});
    if (window.caches?.keys) {
      caches.keys().then((keys) => keys.forEach((k) => caches.delete(k))).catch(() => {});
    }
  });
}

// Auth Context
export const AuthContext = React.createContext();

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing token (authenticated user OR guest session)
    const token = localStorage.getItem('token');
    const authKind = localStorage.getItem('auth_kind');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      if (authKind === 'guest') {
        fetchGuestData();
      } else {
        fetchUserData();
      }
    } else {
      setLoading(false);
    }
  }, []);

  // Hide Emergent badge using CSS injection (safer than DOM manipulation)
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      /* Hide Emergent platform badge */
      a[href*="emergentagent.com"],
      div:has(> a[href*="emergentagent.com"]),
      [class*="emergent"],
      [id*="emergent"] {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
        pointer-events: none !important;
        width: 0 !important;
        height: 0 !important;
        overflow: hidden !important;
      }
    `;
    document.head.appendChild(style);
    return () => style.remove();
  }, []);

  const fetchUserData = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user data:', error);
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const fetchGuestData = async () => {
    try {
      const response = await axios.get(`${API}/guest/me`);
      setUser(response.data); // { role:'guest', id, masked_id, name, is_guest }
    } catch (error) {
      // On expiry, keep a minimal guest shape so the chat screen can show the
      // compassionate expiry overlay instead of the generic login redirect.
      if (error.response?.status === 401 && error.response?.data?.detail === 'guest_expired') {
        setUser({ role: 'guest', is_guest: true, expired: true });
        return;
      }
      localStorage.removeItem('token');
      localStorage.removeItem('auth_kind');
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const guestLogin = (token, guestUser) => {
    localStorage.setItem('token', token);
    localStorage.setItem('auth_kind', 'guest');
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(guestUser);
    setToken(token);
  };

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.removeItem('auth_kind');
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
    setToken(token);
    toast.success('Welcome back!');
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('auth_kind');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setToken(null);
    toast.success('Logged out successfully');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#718096]">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, token, login, guestLogin, logout }}>
      <BrowserRouter>
        <Toaster position="top-center" richColors />
        <Routes>
          {/* Desktop Routes */}
          <Route path="/" element={<Navigate to="/auth" replace />} />
          <Route path="/start" element={<CareSeekerBridge />} />
          <Route path="/bridge" element={<CareSeekerBridge />} />
          <Route path="/guest-chat" element={<GuestChatEntry />} />
          <Route
            path="/auth"
            element={user && user.role !== 'guest' ? <Navigate to={user.role === 'care_seeker' ? '/seeker-dashboard' : (user.role === 'admin' || user.base_role === 'admin') ? '/admin' : '/provider-dashboard'} replace /> : <AuthPage />}
          />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route
            path="/seeker-dashboard"
            element={user && user.role === 'care_seeker' ? <SeekerDashboard /> : <Navigate to="/auth" />}
          />
          <Route
            path="/provider-dashboard"
            element={user && user.role === 'care_provider' ? <ProviderDashboard /> : <Navigate to="/auth" />}
          />
          <Route
            path="/chat/:sessionId"
            element={user ? <ChatInterface /> : <Navigate to="/auth" />}
          />
          <Route
            path="/voice/:sessionId"
            element={user ? <VoiceCallInterface /> : <Navigate to="/auth" />}
          />
          <Route
            path="/book-appointment"
            element={user && user.role === 'care_seeker' ? <BookAppointment /> : <Navigate to="/auth" />}
          />
          <Route
            path="/seeker-intake"
            element={user && user.role === 'care_seeker' ? <SeekerIntakePage /> : <Navigate to="/auth" />}
          />

          {/* Mobile Routes */}
          <Route path="/mobile-chats" element={<MobileChatList />} />
          <Route path="/mobile-chat/:sessionId" element={<MobileChatInterface />} />
          <Route path="/mobile-care-options" element={<MobileCareOptions />} />
          {/* Redirect legacy mobile call routes to the unified VideoCallPage */}
          <Route path="/mobile-voice-call/:sessionId" element={<VideoCallPage />} />
          <Route path="/mobile-video-call/:sessionId" element={<VideoCallPage />} />
          
          {/* Profile & Admin Routes */}
          <Route path="/profile-settings" element={<ProfileSettings />} />
          <Route path="/profile" element={<ProfileSettings />} />
          <Route
            path="/admin"
            element={user && (user.role === 'care_provider' || user.role === 'admin' || user.base_role === 'admin') ? <AdminDashboard /> : <Navigate to="/auth" />}
          />
          
          {/* MVP Admin Routes (JSS) */}
          <Route
            path="/admin/users"
            element={user && (user.role === 'admin' || user.base_role === 'admin') ? <AdminUsersPage /> : <Navigate to="/auth" />}
          />
          <Route
            path="/admin/settings"
            element={user && (user.role === 'admin' || user.base_role === 'admin') ? <AdminSettingsPage /> : <Navigate to="/auth" />}
          />
          <Route
            path="/admin/wellness-partners"
            element={user && (user.role === 'admin' || user.base_role === 'admin') ? <AdminWellnessPartnersPage /> : <Navigate to="/auth" />}
          />
          <Route
            path="/admin/payments"
            element={user && (user.role === 'admin' || user.base_role === 'admin') ? <AdminPaymentsPage /> : <Navigate to="/auth" />}
          />
          <Route
            path="/admin/scheduling"
            element={user && (user.role === 'admin' || user.base_role === 'admin') ? <AdminSchedulingPage /> : <Navigate to="/auth" />}
          />
          <Route
            path="/escalations"
            element={user ? <EscalationsPage /> : <Navigate to="/auth" />}
          />
          
          {/* Info Pages */}
          <Route
            path="/privacy-requests"
            element={user ? <PrivacyRequestsPage /> : <Navigate to="/auth" />}
          />
          <Route
            path="/admin/privacy-requests"
            element={user && (user.role === 'admin' || user.base_role === 'admin') ? <AdminPrivacyRequestsPage /> : <Navigate to="/auth" />}
          />
          <Route path="/tele-manas" element={<TeleManasInfo />} />
          <Route path="/my-resources" element={user && user.role === 'care_seeker' ? <MyResourcesPage /> : <Navigate to="/auth" />} />
          <Route path="/find-therapist" element={<ProviderSearch />} />
          
          {/* Video/Voice Call Page */}
          <Route path="/call/:sessionId" element={<VideoCallPage />} />
        </Routes>
        <AppFooter />
      </BrowserRouter>
    </AuthContext.Provider>
  );
}

export default App;
export { API };